import { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX, Play, Pause, Maximize2, Minimize2 } from "lucide-react";

const VIDEOS = [
  {
    id: "install-05",
    src: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/cx7wpz26_v24044gl0000d2o8jhvog65h6ginmqrg.mp4",
    title: "Flooring Install · In Progress",
    label: "Flooring · Laminate · LVP · Tile",
  },
  {
    id: "install-06",
    src: "https://customer-assets.emergentagent.com/job_hardscape-pro-5/artifacts/xlqx6k44_v24044gl0000d8tbtn7og65h5p6r9ll0.mp4",
    title: "Flooring Craftsmanship",
    label: "Flooring · Tight seams, level finish",
  },
];

export default function VideoShowcase() {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // When switching active video, autoplay + reset state
  useEffect(() => {
    if (!ref.current) return;
    ref.current.load();
    ref.current.muted = muted;
    const p = ref.current.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    setPlaying(true);
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for fullscreen changes (so we update UI when user presses ESC)
  useEffect(() => {
    const onChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(Boolean(fsEl));
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const current = VIDEOS[active];

  const toggleMute = () => {
    if (!ref.current) return;
    ref.current.muted = !ref.current.muted;
    setMuted(ref.current.muted);
  };

  const togglePlay = () => {
    if (!ref.current) return;
    if (ref.current.paused) {
      ref.current.play();
      setPlaying(true);
    } else {
      ref.current.pause();
      setPlaying(false);
    }
  };

  const toggleFullscreen = () => {
    const video = ref.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const fsEl =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;

    if (fsEl) {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.msExitFullscreen;
      if (exit) exit.call(document);
      return;
    }

    // iOS Safari: video element has its own native fullscreen
    if (typeof video.webkitEnterFullscreen === "function") {
      // Unmute so iOS native player has sound when opened
      video.muted = false;
      setMuted(false);
      video.webkitEnterFullscreen();
      return;
    }

    // Standard fullscreen on the container (keeps overlays styled)
    const req =
      container.requestFullscreen ||
      container.webkitRequestFullscreen ||
      container.msRequestFullscreen;
    if (req) {
      req.call(container).catch(() => {});
      // Unmute on fullscreen so audio plays
      if (video.muted) {
        video.muted = false;
        setMuted(false);
      }
    }
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 lg:mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-orange-600 mb-4">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse-dot" />
              WATCH US WORK
            </div>
            <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.025em] text-stone-900">
              See us in
              <br />
              <span className="text-orange-600">action.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-600 text-lg leading-relaxed">
              A look inside recent installs — LVP, laminate, and tile
              flooring. This is the craftsmanship behind every M&amp;L install.
            </p>
          </div>
        </div>

        {/* Featured player */}
        <div
          ref={containerRef}
          data-testid="video-player"
          className="relative group overflow-hidden rounded-3xl bg-stone-900 aspect-video shadow-[0_30px_80px_-20px_rgba(28,25,23,0.35)]"
        >
          <video
            ref={ref}
            key={current.id}
            src={current.src}
            autoPlay
            muted={muted}
            loop
            playsInline
            controlsList="nodownload"
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="showcase-video"
          />

          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-transparent pointer-events-none" />

          {/* Top badge */}
          <div className="absolute top-5 left-5 flex items-center gap-2 bg-stone-50/95 backdrop-blur-md rounded-full px-3.5 py-1.5 text-[10px] font-bold text-stone-900 tracking-wider uppercase">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse-dot" />
            {current.title}
          </div>

          {/* Bottom label */}
          <div className="absolute bottom-5 left-5 text-stone-50">
            <div className="text-[10px] uppercase tracking-wider text-stone-300 font-medium">
              {String(active + 1).padStart(2, "0")} / {String(VIDEOS.length).padStart(2, "0")}
            </div>
            <div className="font-display font-semibold text-xl md:text-2xl mt-1">
              {current.label}
            </div>
          </div>

          {/* Controls */}
          <div className="absolute bottom-5 right-5 flex items-center gap-2">
            <button
              onClick={togglePlay}
              aria-label={playing ? "Pause video" : "Play video"}
              data-testid="video-play-toggle"
              className="w-11 h-11 rounded-full bg-stone-50/95 backdrop-blur-md text-stone-900 flex items-center justify-center hover:bg-orange-500 hover:text-stone-50 transition-colors shadow-lg"
            >
              {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              data-testid="video-mute-toggle"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-stone-50/95 backdrop-blur-md text-stone-900 hover:bg-orange-500 hover:text-stone-50 transition-colors shadow-lg text-sm font-semibold"
            >
              {muted ? (
                <>
                  <VolumeX size={15} />
                  <span className="hidden sm:inline">Tap for sound</span>
                </>
              ) : (
                <>
                  <Volume2 size={15} />
                  <span className="hidden sm:inline">Sound on</span>
                </>
              )}
            </button>
            <button
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              data-testid="video-fullscreen-toggle"
              className="w-11 h-11 rounded-full bg-stone-50/95 backdrop-blur-md text-stone-900 flex items-center justify-center hover:bg-orange-500 hover:text-stone-50 transition-colors shadow-lg"
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </div>
        </div>

        {/* Thumbnail strip */}
        {VIDEOS.length > 1 && (
          <div
            data-testid="video-thumbnails"
            className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {VIDEOS.map((v, i) => (
              <button
                key={v.id}
                onClick={() => setActive(i)}
                data-testid={`video-thumb-${i}`}
                className={`group relative overflow-hidden rounded-2xl aspect-video bg-stone-900 transition-all duration-300 ${
                  active === i
                    ? "ring-2 ring-orange-500 ring-offset-4 ring-offset-white"
                    : "opacity-80 hover:opacity-100"
                }`}
              >
                <video
                  src={v.src}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-stone-950/30 group-hover:bg-stone-950/10 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      active === i
                        ? "bg-orange-500 text-stone-50"
                        : "bg-stone-50/95 text-stone-900 group-hover:bg-orange-500 group-hover:text-stone-50"
                    }`}
                  >
                    {active === i ? <Pause size={14} /> : <Play size={14} className="ml-0.5" />}
                  </div>
                </div>
                <div className="absolute bottom-2 left-3 right-3 text-[10px] uppercase tracking-wider font-semibold text-stone-50">
                  {v.title}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
